import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminAPI } from '../services/api'
import Spinner from './Spinner'
import { apiErrorMessage } from '../utils/apiError'
import type {
  DiagnosisConversations,
  DiagnosisHypothesis,
  DiagnosisTurn,
} from '../types/adminDiagnostics'

/**
 * Admin engine diagnosis, stage 2: ask why the engine behaved as it did.
 *
 * Every guard lives on the server. What arrives here is already validated:
 * figures resolved from the frozen data block, failing items dropped, and the
 * "unverified" label written by the server on each hypothesis. This panel
 * renders that response as-is. It never adds a figure, a label or a link the
 * server did not send, and it keeps no history of its own beyond display --
 * a follow-up sends only the conversation id and the question.
 */

const QUESTION_MAX = 500

/**
 * Shown above the tool, always. The diagnosis is superuser-only and its hard
 * guards cover figures, confident predictions and the unverified labels; its
 * conversational wording is not held to a zero-steering bar
 * (.fix3-work/stage2-eval.md), so the framing is stated plainly here.
 */
export const DISCLAIMER =
  'Admin diagnostic tool. Describes the engine and proposes hypotheses to test. Not financial advice; not for users. Verify every figure and hypothesis before acting.'

/** Display names for the server's test kinds; unknown kinds show raw. */
const TEST_KIND_LABELS: Record<string, string> = {
  exit_backtest: 'Exit backtest',
  regime_replay: 'Regime replay',
  counter_query: 'Gate counters',
  record_query: 'Track record',
  config_diff: 'Config comparison',
  manual: 'Manual check',
}

const fmtClock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

const STATUS_LABELS: Record<string, string> = {
  answered: 'Answered',
  declined: 'Declined',
  unavailable: 'Unavailable',
  ended: 'Ended',
}

const HypothesisCard = ({ h, index }: { h: DiagnosisHypothesis; index: number }) => (
  <li className="rounded-lg border border-gray-200 bg-white p-4" data-testid="diagnosis-hypothesis">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <p className="font-medium text-gray-900">
        H{index + 1}. {h.cause}
      </p>
      {h.label && (
        <span
          className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900"
          data-testid="hypothesis-label"
        >
          {h.label}
        </span>
      )}
    </div>
    {h.supporting_observations.length > 0 && (
      <p className="mt-1 text-xs text-gray-500">
        Rests on {h.supporting_observations.map((i) => `O${i + 1}`).join(', ')}
      </p>
    )}
    <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Test: {TEST_KIND_LABELS[h.test.kind] ?? h.test.kind}
      </p>
      <p className="mt-1">{h.test.method}</p>
      <p className="mt-1">
        <span className="font-medium">Confirms if:</span> {h.test.confirms_if}
      </p>
      <p>
        <span className="font-medium">Refutes if:</span> {h.test.refutes_if}
      </p>
      {h.test.link ? (
        <Link to={h.test.link} className="mt-2 inline-block text-accent hover:underline" data-testid="hypothesis-test-link">
          Open {TEST_KIND_LABELS[h.test.kind] ?? 'the test tool'} →
        </Link>
      ) : (
        <p className="mt-2 text-xs text-gray-500">No built-in tool for this test.</p>
      )}
    </div>
    <p className="mt-3 text-sm text-gray-700">
      <span className="font-medium">Risk if acted on untested:</span> {h.risk_if_acted_on_untested}
    </p>
  </li>
)

const TurnView = ({ turn, question }: { turn: DiagnosisTurn; question: string }) => (
  <article className="space-y-3" data-testid={`diagnosis-turn-${turn.status}`}>
    <p className="text-sm text-gray-500">
      <span className="font-medium text-gray-700">You asked:</span> {question || 'Diagnose now'}
    </p>

    {turn.status === 'declined' && (
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="diagnosis-declined">
        {turn.reply}
      </div>
    )}

    {turn.status === 'unavailable' && (
      <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700" data-testid="diagnosis-unavailable">
        <p className="font-semibold text-gray-900">Diagnosis unavailable</p>
        <p className="mt-1">{turn.reply}</p>
      </div>
    )}

    {turn.status === 'ended' && (
      <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700" data-testid="diagnosis-ended">
        {turn.reply}
      </div>
    )}

    {turn.status === 'answered' && (
      <>
        <p className="text-gray-900" data-testid="diagnosis-reply">
          {turn.reply}
        </p>

        {turn.observations.length > 0 && (
          <section aria-label="Observations">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Observations</h4>
            <ol className="mt-2 space-y-2">
              {turn.observations.map((o, i) => (
                <li key={i} className="rounded-lg border border-gray-200 bg-white p-3 text-sm" data-testid="diagnosis-observation">
                  <p className="text-gray-800">
                    <span className="font-medium">O{i + 1}.</span> {o.text}
                  </p>
                  {o.evidence.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1">
                      {o.evidence.map((e) => (
                        <li
                          key={e.fact}
                          className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600"
                          data-testid="diagnosis-evidence"
                        >
                          {e.fact}: {e.value}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {turn.hypotheses.length > 0 && (
          <section aria-label="Hypotheses">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Hypotheses</h4>
            <ol className="mt-2 space-y-2">
              {turn.hypotheses.map((h, i) => (
                <HypothesisCard key={i} h={h} index={i} />
              ))}
            </ol>
          </section>
        )}

        {turn.insufficient_data.length > 0 && (
          <section aria-label="What the data can't show">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">What the data can&apos;t show</h4>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
              {turn.insufficient_data.map((item, i) => (
                <li key={i} data-testid="diagnosis-insufficient">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}
      </>
    )}
  </article>
)

interface ThreadEntry {
  question: string
  turn: DiagnosisTurn
}

const DiagnosisPanel = () => {
  const [thread, setThread] = useState<ThreadEntry[]>([])
  const [question, setQuestion] = useState('')
  const [followUp, setFollowUp] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limitReached, setLimitReached] = useState(false)
  const [summary, setSummary] = useState<DiagnosisConversations | null>(null)

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await adminAPI.diagnosisConversations())
    } catch {
      // The counter and history are secondary; the panel still works.
    }
  }, [])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const conversationId = thread.length > 0 ? thread[thread.length - 1].turn.conversation_id : null
  const lastStatus = thread.length > 0 ? thread[thread.length - 1].turn.status : null
  const dataAsOf = thread.find((e) => e.turn.data_as_of)?.turn.data_as_of ?? null
  const callsLeft = thread.length > 0 ? thread[thread.length - 1].turn.calls_left_today : summary?.calls_left_today

  const ask = async (text: string, continuing: boolean) => {
    setPending(true)
    setError(null)
    setLimitReached(false)
    try {
      const body: { question?: string; conversation_id?: string } = {}
      if (text.trim()) body.question = text.trim()
      if (continuing && conversationId) body.conversation_id = conversationId
      const turn = await adminAPI.diagnose(body)
      setThread((prev) => (continuing ? [...prev, { question: text.trim(), turn }] : [{ question: text.trim(), turn }]))
      if (continuing) setFollowUp('')
      else setQuestion('')
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 429) setLimitReached(true)
      setError(apiErrorMessage(err, 'The diagnosis could not run. Please try again.'))
    } finally {
      setPending(false)
      loadSummary()
    }
  }

  /** Re-open a past conversation from the history list, as it was shown. */
  const openConversation = async (conversationId: string) => {
    setPending(true)
    setError(null)
    setLimitReached(false)
    try {
      const detail = await adminAPI.diagnosisConversation(conversationId)
      setThread(
        detail.turns.map((t) => ({
          question: t.question,
          turn: { ...t, calls_left_today: detail.calls_left_today },
        })),
      )
    } catch (err) {
      setError(apiErrorMessage(err, 'That conversation could not be loaded.'))
    } finally {
      setPending(false)
    }
  }

  const onStart = (e: FormEvent) => {
    e.preventDefault()
    ask(question, false)
  }

  const onFollowUp = (e: FormEvent) => {
    e.preventDefault()
    if (followUp.trim()) ask(followUp, true)
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Diagnose">
      <div className="border-b border-gray-200 p-4">
        <p
          role="note"
          data-testid="diagnosis-disclaimer"
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          {DISCLAIMER}
        </p>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Diagnose</h2>
          <p className="text-xs text-gray-500" data-testid="diagnosis-meta">
            {dataAsOf && <>Data as of {fmtClock(dataAsOf)} · </>}
            {callsLeft !== undefined && (
              <span data-testid="diagnosis-calls-left">
                {callsLeft} {callsLeft === 1 ? 'call' : 'calls'} left today
              </span>
            )}
          </p>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Ask why something happened. The answer reads only FinCreator&apos;s own record, gates and scans, and offers
          hypotheses to test. It uses an AI model; nothing here changes the engine.
        </p>
        <form onSubmit={onStart} className="mt-3 flex flex-col gap-2 sm:flex-row">
          <label htmlFor="diagnosis-question" className="sr-only">
            Ask why something happened
          </label>
          <input
            id="diagnosis-question"
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={QUESTION_MAX}
            placeholder="Ask why something happened, or leave blank for a general diagnosis"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={pending}
          />
          <button
            type="submit"
            disabled={pending}
            className="flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2 font-semibold text-terminal-bg hover:bg-accent-hover disabled:opacity-50"
          >
            {pending && <Spinner size="sm" />}
            {thread.length > 0 ? 'New diagnosis' : 'Diagnose'}
          </button>
        </form>
      </div>

      <div className="space-y-6 p-4">
        {error && (
          <div
            role="alert"
            data-testid={limitReached ? 'diagnosis-limit-reached' : 'diagnosis-error'}
            className={`rounded-lg border p-4 text-sm ${
              limitReached ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-danger-200 bg-danger-50 text-danger-700'
            }`}
          >
            {limitReached && <p className="font-semibold">Limit reached</p>}
            <p>{error}</p>
          </div>
        )}

        {pending && (
          <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="diagnosis-loading">
            <Spinner size="sm" /> Working on it… this can take a minute.
          </div>
        )}

        {thread.map((entry, i) => (
          <TurnView key={`${entry.turn.conversation_id}-${entry.turn.turn}-${i}`} turn={entry.turn} question={entry.question} />
        ))}

        {thread.length > 0 && lastStatus !== 'ended' && (
          <form onSubmit={onFollowUp} className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row">
            <label htmlFor="diagnosis-follow-up" className="sr-only">
              Follow-up question
            </label>
            <input
              id="diagnosis-follow-up"
              type="text"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              maxLength={QUESTION_MAX}
              placeholder="Ask a follow-up"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
              disabled={pending}
            />
            <button
              type="submit"
              disabled={pending || !followUp.trim()}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
            >
              Follow up
            </button>
          </form>
        )}
      </div>

      <div className="border-t border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900">Recent diagnoses</h3>
        {!summary || summary.conversations.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500">None yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 text-sm" data-testid="diagnosis-history">
            {summary.conversations.map((c) => (
              <li key={c.conversation_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-gray-700">
                <span>{c.question || 'Diagnose now'}</span>
                <span className="flex items-center gap-3 text-gray-500">
                  {c.status ? STATUS_LABELS[c.status] ?? c.status : '—'} · {fmtWhen(c.started_at)}
                  <button
                    type="button"
                    onClick={() => openConversation(c.conversation_id)}
                    disabled={pending}
                    className="text-accent hover:underline disabled:opacity-50"
                    data-testid={`open-conversation-${c.conversation_id}`}
                  >
                    Open
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

export default DiagnosisPanel
